<template>
  <BaseSimpleLayout class="role-permission-create-detail">
    <template #title>ロール追加</template>
    <template #button-area>
      <el-button type="info" @click="router.push('/master/role-permission')">
        戻る
      </el-button>
      <el-button type="primary" :disabled="isSaving" @click="createRole">
        作成
      </el-button>
    </template>

    <template #main>
      <div v-loading="isLoading" class="edit-form">
        <div class="edit-form__info">
          <el-form label-width="130px">
            <el-form-item label="ロールコード" required :error="roleCodeError">
              <el-input
                v-model="roleCode"
                maxlength="50"
                placeholder="例: STOREADMIN"
                @input="onRoleCodeInput"
              />
            </el-form-item>
            <el-form-item label="ロール名" required :error="roleNameError">
              <el-input
                v-model="roleName"
                maxlength="100"
                placeholder="例: 店舗管理者"
                @input="roleNameError = ''"
              />
            </el-form-item>
            <el-form-item
              label="ホーム画面"
              class="home-path-form-item"
              required
              :show-message="false"
              :error="homePathDraftError"
            >
              <el-select
                v-model="homePathDraft"
                class="home-path-select"
                @change="homePathDraftError = ''"
              >
                <el-option
                  v-for="homePathOption in HOME_PATH_OPTIONS"
                  :key="homePathOption.path"
                  :label="homePathOption.label"
                  :value="homePathOption.path"
                />
              </el-select>
              <div
                v-if="homePathDraftError"
                class="home-path-error-block"
                role="alert"
              >
                {{ homePathDraftError }}
              </div>
            </el-form-item>
            <el-form-item label="ロール種別">
              <el-radio-group v-model="roleLevel">
                <el-radio v-if="canSelectAdminRole" :label="2">管理者</el-radio>
                <el-radio :label="1">担当者</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item v-if="roleLevel === 1" label="親ロール">
              <el-checkbox-group
                v-model="parentRoleCheckboxIds"
                class="managed-employee-checkbox-group"
                @change="onParentRoleCheckboxChange"
              >
                <el-checkbox
                  v-for="role in staffParentRoleOptions"
                  :key="role.id"
                  :label="role.id"
                  :disabled="role.level === 3"
                >
                  {{ role.name }}
                </el-checkbox>
              </el-checkbox-group>
            </el-form-item>
            <el-form-item v-if="roleLevel === 2" label="管理対象担当者">
              <el-checkbox-group
                v-model="managedChildRoleIds"
                class="managed-employee-checkbox-group"
              >
                <el-checkbox
                  v-for="role in managedEmployeeOptions"
                  :key="role.id"
                  :label="role.id"
                >
                  {{ role.name }}
                </el-checkbox>
              </el-checkbox-group>
            </el-form-item>
          </el-form>
        </div>

        <div class="edit-form__table">
          <el-table
            :data="permissionRows"
            row-key="id"
            :tree-props="{ children: 'children' }"
            border
            stripe
            :max-height="tableHeight"
            class="permission-table"
          >
            <el-table-column
              prop="name"
              label="機能（画面一覧）"
              fixed
              min-width="320"
            />
            <el-table-column
              :label="roleName || '新規ロール権限'"
              width="180"
              align="center"
            >
              <template #default="scope">
                <RolePermissionMatrixCell
                  :row-kind="scope.row.kind"
                  :model-value="scope.row.permission"
                  :status="scope.row.permission"
                  :changed="false"
                  :options="permissionOptions"
                  mode="edit"
                  @update:model-value="updatePermission(scope.row.id, $event)"
                />
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <BaseConfirmDialog
        v-model="createConfirmVisible"
        title="作成の確認"
        message="この内容でロールを作成します。よろしいですか？"
        confirm-text="作成"
        cancel-text="キャンセル"
        confirm-type="primary"
        :loading="isSaving"
        @confirm="confirmCreateRole"
      />
    </template>
  </BaseSimpleLayout>
</template>

<script setup lang="ts">
  import { onMounted, ref, watch } from 'vue';
  import { ElMessage } from 'element-plus';
  import { useRoute } from 'vue-router';
  import BaseConfirmDialog from '@/components/BaseConfirmDialog.vue';
  import BaseSimpleLayout from '@/components/BaseSimpleLayout.vue';
  import RolePermissionMatrixCell from '@/pages/master/role-permission/RolePermissionMatrixCell.vue';
  import {
    DEFAULT_ROLE_HOME_PATH,
    HOME_PATH_OPTIONS,
    resolveRoleHomePathForEdit,
    validateHomePathAgainstPermissions
  } from '@/pages/master/role-permission/homePathConfig';
  import {
    buildParentRoleIdsForSave,
    buildStaffParentRoleOptions,
    defaultParentRoleCheckboxIds,
    normalizeParentRoleCheckboxIds
  } from '@/pages/master/role-permission/parentRoleHelpers';
  import router from '@/router';
  import store from '@/store';
  import type {
    ActionKey,
    RolePermissionDetail,
    RolePermissionRole,
    RolePermissionScreen
  } from '@/store/modules/rolePermission';

  type PermissionValue = 'full' | 'edit' | 'view' | 'limited' | 'none';
  type PermissionRow = {
    id: string;
    name: string;
    kind: 'screen' | 'button';
    permission: PermissionValue;
    screenId?: number;
    children?: PermissionRow[];
  };

  const route = useRoute();
  const roleCode = ref('');
  const roleName = ref('');
  const roleCodeError = ref('');
  const roleNameError = ref('');
  const roleLevel = ref<1 | 2>(1);
  const managedChildRoleIds = ref<number[]>([]);
  const parentRoleCheckboxIds = ref<number[]>([]);
  const staffParentRoleOptions = ref<RolePermissionRole[]>([]);
  const allRoleDefinitions = ref<RolePermissionRole[]>([]);
  const homePathDraft = ref<string>(DEFAULT_ROLE_HOME_PATH);
  const homePathDraftError = ref('');
  const screensCatalog = ref<RolePermissionScreen[]>([]);
  const tableHeight = window.innerHeight - 260;
  const managedEmployeeOptions = ref<RolePermissionRole[]>([]);
  const permissionRows = ref<PermissionRow[]>([]);
  const isLoading = ref(false);
  const isSaving = ref(false);
  const createConfirmVisible = ref(false);
  const canSelectAdminRole = ref(false);

  /** ロールコードは大文字の英字（A～Z）とアンダースコアのみ */
  const normalizeRoleCode = (raw: string) =>
    raw.toUpperCase().replace(/[^A-Z_]/g, '');

  const onRoleCodeInput = (val: string) => {
    roleCode.value = normalizeRoleCode(val);
    roleCodeError.value = '';
  };

  const permissionOptions = [
    { value: 'full', label: '登録/訂正/削除可' },
    { value: 'edit', label: '訂正可' },
    { value: 'view', label: '閲覧のみ' },
    { value: 'none', label: '権限無し' }
  ] as { value: PermissionValue; label: string }[];

  const permissionToActionKey = (value: PermissionValue): ActionKey => {
    if (value === 'full') return 'CRUD';
    if (value === 'edit') return 'EDIT';
    if (value === 'view') return 'VIEW';
    return 'NONE';
  };
  const actionKeyToPermission = (actionKey: ActionKey): PermissionValue => {
    if (actionKey === 'CRUD') return 'full';
    if (actionKey === 'EDIT') return 'edit';
    if (actionKey === 'VIEW') return 'view';
    return 'none';
  };
  const buildPermissionRows = (
    screens: RolePermissionScreen[],
    permissionMap: Record<number, PermissionValue>
  ): PermissionRow[] =>
    screens.map((screenRoot) => ({
      id: String(screenRoot.id),
      name: screenRoot.name,
      kind: 'screen',
      screenId: screenRoot.id,
      permission: permissionMap[screenRoot.id] ?? 'none',
      children: screenRoot.childs.map((screenChild) => ({
        id: String(screenChild.id),
        name: screenChild.name,
        kind: 'button',
        screenId: screenChild.id,
        permission: permissionMap[screenChild.id] ?? 'none'
      }))
    }));
  const applyCopyFromRole = async (
    copyFromId: number,
    screens: RolePermissionScreen[]
  ) => {
    const detail = (await store.dispatch(
      'rolePermission/fetchRoleDetail',
      copyFromId
    )) as RolePermissionDetail | null;
    if (!detail) return;

    // 複製時はホーム画面と権限（画面）のみを引き継ぐ。
    homePathDraft.value = resolveRoleHomePathForEdit(
      detail.homePath,
      detail.homePathDraft
    );

    const sourcePermissions =
      (detail.permissionsDraft?.length ?? 0) > 0
        ? detail.permissionsDraft
        : detail.permissionsMain;
    const permissionMap = (sourcePermissions ?? []).reduce(
      (acc, row) => ({
        ...acc,
        [row.screenId]: actionKeyToPermission(row.actionKey)
      }),
      {} as Record<number, PermissionValue>
    );
    permissionRows.value = buildPermissionRows(screens, permissionMap);
  };
  const findRowById = (
    rows: PermissionRow[],
    id: string
  ): PermissionRow | null => {
    for (const row of rows) {
      if (row.id === id) return row;
      if (row.children?.length) {
        const child = findRowById(row.children, id);
        if (child) return child;
      }
    }
    return null;
  };
  const collectPermissionDraft = (rows: PermissionRow[]) => {
    const result: { screenId: number; actionKey: ActionKey }[] = [];
    const walk = (nodes: PermissionRow[]) => {
      nodes.forEach((permissionRowNode) => {
        if (typeof permissionRowNode.screenId === 'number') {
          result.push({
            screenId: permissionRowNode.screenId,
            actionKey: permissionToActionKey(permissionRowNode.permission)
          });
        }
        if (permissionRowNode.children?.length) {
          walk(permissionRowNode.children);
        }
      });
    };
    walk(rows);
    return result;
  };
  const updatePermission = (rowId: string, value: PermissionValue) => {
    const row = findRowById(permissionRows.value, rowId);
    if (!row) return;
    row.permission = value;
  };

  const createRole = async () => {
    roleCodeError.value = '';
    roleNameError.value = '';
    homePathDraftError.value = '';

    const trimmedRoleCode = roleCode.value.trim();
    if (!trimmedRoleCode) {
      roleCodeError.value = 'ロールコードを入力してください。';
    } else if (!/^[A-Z_]+$/.test(trimmedRoleCode)) {
      roleCodeError.value =
        'ロールコードは大文字の英字（A～Z）とアンダースコア（_）のみ入力できます。';
    }
    if (!roleName.value.trim()) {
      roleNameError.value = 'ロール名を入力してください。';
    }
    const homePathValidationMessage = validateHomePathAgainstPermissions(
      homePathDraft.value,
      screensCatalog.value,
      permissionRows.value,
      'permission'
    );
    if (homePathValidationMessage) {
      homePathDraftError.value = homePathValidationMessage;
    }
    if (
      roleCodeError.value ||
      roleNameError.value ||
      homePathDraftError.value
    ) {
      return;
    }
    createConfirmVisible.value = true;
  };
  const confirmCreateRole = async () => {
    const homePathValidationMessage = validateHomePathAgainstPermissions(
      homePathDraft.value,
      screensCatalog.value,
      permissionRows.value,
      'permission'
    );
    if (homePathValidationMessage) {
      homePathDraftError.value = homePathValidationMessage;
      createConfirmVisible.value = false;
      return;
    }
    isSaving.value = true;
    try {
      await store.dispatch('rolePermission/createRole', {
        code: roleCode.value.trim(),
        name: roleName.value.trim(),
        level: roleLevel.value,
        managedChildRoleIds:
          roleLevel.value === 2 ? managedChildRoleIds.value : [],
        parentRoleIds: buildParentRoleIdsForSave(
          roleLevel.value,
          parentRoleCheckboxIds.value,
          allRoleDefinitions.value
        ),
        homePathDraft: homePathDraft.value.trim() || DEFAULT_ROLE_HOME_PATH,
        permissionsDraft: collectPermissionDraft(permissionRows.value)
      });
      ElMessage.success('ロールを作成しました。');
      router.push('/master/role-permission');
    } finally {
      createConfirmVisible.value = false;
      isSaving.value = false;
    }
  };

  const onParentRoleCheckboxChange = (values: number[]) => {
    parentRoleCheckboxIds.value = normalizeParentRoleCheckboxIds(
      values,
      staffParentRoleOptions.value
    );
  };

  watch(roleLevel, (level) => {
    if (level !== 1 || !allRoleDefinitions.value.length) return;
    parentRoleCheckboxIds.value = defaultParentRoleCheckboxIds(
      allRoleDefinitions.value
    );
  });

  onMounted(async () => {
    // 担当者（roleLevel = 1）はロール追加画面へアクセスできない。
    if (Number(store.getters['auth/auth'].roleLevel ?? 0) <= 1) {
      await router.replace('/master/role-permission');
      return;
    }
    isLoading.value = true;
    try {
      const [screens, roles] = await Promise.all([
        store.dispatch('rolePermission/fetchScreens'),
        store.dispatch('rolePermission/fetchRoles')
      ]);
      const rolePermissionScreens = screens as RolePermissionScreen[];
      allRoleDefinitions.value = roles as RolePermissionRole[];
      staffParentRoleOptions.value = buildStaffParentRoleOptions(
        allRoleDefinitions.value
      );
      parentRoleCheckboxIds.value = defaultParentRoleCheckboxIds(
        allRoleDefinitions.value
      );
      screensCatalog.value = rolePermissionScreens;
      {
        const staffRolesAtLevelOne = allRoleDefinitions.value.filter(
          (roleDefinition) => roleDefinition.level === 1
        );
        const currentAuthRoleId = Number(store.getters['auth/auth'].role ?? 0);
        const loggedInUserRoleDefinition = allRoleDefinitions.value.find(
          (roleDefinition) => roleDefinition.id === currentAuthRoleId
        );
        if (!loggedInUserRoleDefinition) {
          await router.replace('/top');
          return;
        }
        canSelectAdminRole.value =
          Number(loggedInUserRoleDefinition?.level) === 3;
        const managedChildRoleIdSetForLoggedInUser = new Set(
          loggedInUserRoleDefinition.managedChildRoleIds ?? []
        );
        managedEmployeeOptions.value = staffRolesAtLevelOne.filter(
          (roleDefinition) =>
            managedChildRoleIdSetForLoggedInUser.has(roleDefinition.id)
        );
      }
      permissionRows.value = buildPermissionRows(rolePermissionScreens, {});

      const copyFromId = Number(route.query.copyFrom ?? 0);
      if (Number.isFinite(copyFromId) && copyFromId > 0) {
        await applyCopyFromRole(copyFromId, rolePermissionScreens);
      }
    } finally {
      isLoading.value = false;
    }
  });
</script>

<style lang="scss">
  .role-permission-create-detail {
    .edit-form {
      display: flex;
      gap: 10%;
      padding-top: 16px;

      &__info {
        flex: 3 1 0;
        min-width: 0;

        .el-input,
        .el-select {
          width: 100%;
        }

        :deep(.el-form-item__error) {
          padding-top: 4px;
          padding-left: 2px;
        }
      }

      &__table {
        flex: 7 1 0;
        min-width: 0;
      }
    }

    .managed-employee-checkbox-group {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .home-path-error-block {
      display: block;
      width: 100%;
      color: var(--el-color-danger, #f56c6c);
      font-size: 13px;
      line-height: 1.65;
      text-align: left;
      word-break: break-word;
    }
  }
</style>
